import { useEffect, useMemo, useState, type ChangeEvent, type FormEvent } from "react";
import { useParams } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import StatCard from "../components/StatCard";
import Drawer from "../components/Drawer";
import Modal from "../components/Modal";
import COLORS from "../utils/Colors";
import DetalleAnalisisGrupos, { type Grupo as GrupoCardData } from "@/components/layouts/DetalleAnalisisGrupos";
import DetalleAnalisisEstudiantes, { type EstudianteTablaRow } from "@/components/layouts/DetalleAnalisisEstudiantes";
import DetalleAnalisisDatos from "@/components/layouts/DetalleAnalisisDatos";
import DetalleAnalisisReportes from "@/components/layouts/DetalleAnalisisReportes";
import { databaseService, ApiError } from "../services/databaseService";
import { CATEGORIA_LABELS, CATEGORIAS } from "../utils/categorias";
import type {
  EvaluacionConProgreso,
  EstudianteConEstado,
  FormularioConTotalPreguntas,
  GrupoConProgreso,
  GrupoRespuestas,
  ImportacionEstudiantes,
  Reporte,
  ResultadoReporteBulk,
} from "../utils/types";
import ClipboardCopy from "@/components/ClipboardCopy";


// ── Tipos locales del formulario de grupo ───────────────────────
interface AlumnoFormRow {
  id: number;
  nombreCompleto: string;
  curp: string;
}

interface GrupoFormState {
  nombre: string;
  formEmocionesId: string;
  formBienpsicId: string;
  formAprendizajeId: string;
  alumnosNuevos: AlumnoFormRow[];
}

const createAlumnoRow = (): AlumnoFormRow => ({ id: Date.now() + Math.random(), nombreCompleto: "", curp: "" });
const emptyGrupoForm = (): GrupoFormState => ({
  nombre: "", formEmocionesId: "", formBienpsicId: "", formAprendizajeId: "", alumnosNuevos: [createAlumnoRow()],
});

// ── Adaptadores de datos reales → props de los sub-componentes ──
const ESTADO_GRUPO_MAP: Record<string, "completo" | "en_progreso" | "sin_iniciar"> = {
  completo: "completo",
  en_progreso: "en_progreso",
  pendiente: "sin_iniciar",
  sin_alumnos: "sin_iniciar",
};

function mapGrupoParaCard(g: GrupoConProgreso): GrupoCardData {
  const formularios = [
    g.formEmocionesTitulo ? CATEGORIA_LABELS.emociones : null,
    g.formBienpsicTitulo ? CATEGORIA_LABELS.bienestar_psicologico : null,
    g.formAprendizajeTitulo ? CATEGORIA_LABELS.aprendizaje : null,
  ].filter((v): v is string => v !== null);

  return {
    id: g.grupoId,
    nombre: g.grupoNombre,
    estado: ESTADO_GRUPO_MAP[g.estadoGrupo] ?? "sin_iniciar",
    sesionesCompletadas: Number(g.sesionesCompletadas),
    sesionesPosibles: Number(g.totalAlumnos) * 3,
    reportesPublicados: Number(g.reportesIndividuales),
    totalReportes: Number(g.totalAlumnos),
    reporteGrupal: Number(g.reporteGrupal) > 0,
    formularios,
  };
}

function formatFecha(fecha: string) {
  // Postgres serializa DATE como ISO datetime completo en UTC medianoche
  // (...T00:00:00.000Z). Hay que formatear en UTC también, si no el
  // navegador la corre un día para atrás en timezones negativos.
  const iso = fecha.includes("T") ? fecha : `${fecha}T00:00:00Z`;
  return new Date(iso).toLocaleDateString("es-ES", { day: "numeric", month: "long", year: "numeric", timeZone: "UTC" });
}

type EstadoEvaluacionUI = "cerrado" | "abierto" | "publico";

function estadoEvaluacionUI(evaluacion: EvaluacionConProgreso): EstadoEvaluacionUI {
  return evaluacion.estado;
}

const ESTADO_UI_LABELS: Record<EstadoEvaluacionUI, string> = {
  cerrado: "Cerrado",
  abierto: "Abierto",
  publico: "Público",
};

type NextEstadoProps = {
  evaluacionId: string | undefined;
  estado: EstadoEvaluacionUI;
  cargarEvaluacion: () => void;
};

function NextEstadoBtn({ evaluacionId, estado, cargarEvaluacion }: NextEstadoProps) {
  // ── Fase de la evaluación ─────────────────────────────────────
  const cambiarFase = async (siguienteEstado: "cerrado" | "abierto" | "publico") => {
    if (!evaluacionId) return;
    try {
      await databaseService.admin.cambiarEstadoEvaluacion(evaluacionId, siguienteEstado);
      cargarEvaluacion();
    } catch (err) {
      alert(err instanceof ApiError ? err.message : "No se pudo cambiar el estado de la evaluación");
    }
  };

  return (
    <>
      {estado === "cerrado" && (
        <button onClick={() => cambiarFase("abierto")} style={{ padding: "7px 12px", borderRadius: 8, border: "none", background: COLORS.azul400, color: "#fff", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
          Abrir evaluación
        </button>
      )}

      {estado === "abierto" && (
        <button onClick={() => cambiarFase("publico")} style={{ padding: "7px 12px", borderRadius: 8, border: "none", background: COLORS.verde400, color: "#fff", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
          Publicar evaluación
        </button>
      )}

      {estado === "publico" && (
        <button onClick={() => cambiarFase("cerrado")} style={{ padding: "7px 12px", borderRadius: 8, border: "none", background: COLORS.violeta400, color: "#fff", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
          Cerrar evaluación
        </button>
      )}
    </>
  );
}

// ── Página principal ──────────────────────────────────────────
export default function DetalleAnalisis() {
  const { id: evaluacionId } = useParams<{ id: string }>();

  const [evaluacion, setEvaluacion] = useState<EvaluacionConProgreso | null>(null);
  const [grupos, setGrupos] = useState<GrupoConProgreso[]>([]);
  const [loadingEvaluacion, setLoadingEvaluacion] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [layout, setLayout] = useState<"grupos" | "estudiantes" | "datos" | "reportes">("grupos");

  const [estudiantesTabla, setEstudiantesTabla] = useState<EstudianteTablaRow[] | null>(null);
  const [loadingEstudiantes, setLoadingEstudiantes] = useState(false);

  const [datosRespuestas, setDatosRespuestas] = useState<{
    preguntas: GrupoRespuestas["preguntas"];
    estudiantes: Array<GrupoRespuestas["estudiantes"][number] & { grupoNombre: string }>;
  } | null>(null);
  const [grupoResultadosId, setGrupoResultadosId] = useState("");
  const [loadingDatos, setLoadingDatos] = useState(false);

  const [showCrearGrupo, setShowCrearGrupo] = useState(false);
  const [grupoEditandoId, setGrupoEditandoId] = useState<string | null>(null);
  const [grupoEditandoEstudiantes, setGrupoEditandoEstudiantes] = useState<EstudianteConEstado[]>([]);
  const [loadingDrawer, setLoadingDrawer] = useState(false);
  const [grupoForm, setGrupoForm] = useState<GrupoFormState>(emptyGrupoForm());
  const [formularios, setFormularios] = useState<FormularioConTotalPreguntas[]>([]);
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importando, setImportando] = useState(false);
  const [importResultado, setImportResultado] = useState<ImportacionEstudiantes | null>(null);
  const [subiendoReportesBulk, setSubiendoReportesBulk] = useState(false);
  const [resultadoReportesBulk, setResultadoReportesBulk] = useState<{
    resultados: ResultadoReporteBulk[];
    asignados: number;
  } | null>(null);

  const cargarEvaluacion = () => {
    if (!evaluacionId) return;
    setLoadingEvaluacion(true);
    setError(null);
    databaseService.admin.obtenerEvaluacion(evaluacionId)
      .then(data => {
        setEvaluacion(data);
        setGrupos(data.grupos);
        setGrupoResultadosId(current =>
          data.grupos.some(grupo => grupo.grupoId === current)
            ? current
            : data.grupos[0]?.grupoId ?? ""
        );
      })
      .catch(err => setError(err instanceof ApiError ? err.message : "No se pudo cargar la evaluación"))
      .finally(() => setLoadingEvaluacion(false));
  };

  useEffect(cargarEvaluacion, [evaluacionId]);

  // Invalida la caché de las pestañas "estudiantes"/"datos" cuando cambian los grupos
  const invalidarTabs = () => {
    setEstudiantesTabla(null);
    setDatosRespuestas(null);
  };

  const cargarEstudiantesTabla = async () => {
    if (!evaluacionId) return;
    setLoadingEstudiantes(true);
    try {
      const [detalles, reportes] = await Promise.all([
        Promise.all(grupos.map(g => databaseService.admin.obtenerGrupo(g.grupoId))),
        databaseService.admin.listarReportes({ evaluacionId, tipo: "individual" }),
      ]);
      const reportePorEstudiante = new Map<string, Reporte>(reportes.map(r => [r.estudianteId ?? "", r]));

      const filas: EstudianteTablaRow[] = detalles.flatMap(g =>
        g.estudiantes.map(e => ({
          estudianteId: e.estudianteId,
          nombreCompleto: e.nombreCompleto,
          curp: e.curp,
          grupoId: g.grupoId,
          grupoNombre: g.grupoNombre,
          todoCompletado: e.todoCompletado,
          archivoReporte: reportePorEstudiante.get(e.estudianteId)?.archivoUrl ?? null,
        }))
      );
      setEstudiantesTabla(filas);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "No se pudieron cargar los estudiantes");
    } finally {
      setLoadingEstudiantes(false);
    }
  };

  useEffect(() => {
    if (!evaluacion) return;
    if (layout === "estudiantes" && estudiantesTabla === null && !loadingEstudiantes) cargarEstudiantesTabla();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layout, evaluacion]);

  useEffect(() => {
    if (layout !== "datos" || !grupoResultadosId || datosRespuestas !== null) return;

    let vigente = true;
    const grupoNombre = grupos.find(grupo => grupo.grupoId === grupoResultadosId)?.grupoNombre ?? "";
    setLoadingDatos(true);
    databaseService.admin.obtenerRespuestasGrupo(grupoResultadosId)
      .then(data => {
        if (vigente) {
          setDatosRespuestas({
            preguntas: data.preguntas,
            estudiantes: data.estudiantes.map(estudiante => ({ ...estudiante, grupoNombre })),
          });
        }
      })
      .catch(err => {
        if (vigente) setError(err instanceof ApiError ? err.message : "No se pudieron cargar las respuestas");
      })
      .finally(() => {
        if (vigente) setLoadingDatos(false);
      });

    return () => { vigente = false; };
  }, [layout, grupoResultadosId, grupos, datosRespuestas]);

  const totalAlumnos = grupos.reduce((s, g) => s + Number(g.totalAlumnos), 0);
  const totalReportes = grupos.reduce((s, g) => s + Number(g.reportesIndividuales), 0);
  const porcentajeCompletado = totalAlumnos > 0
    ? Math.round((grupos.reduce((s, g) => s + Number(g.sesionesCompletadas), 0) / (totalAlumnos * 3)) * 100)
    : 0;

  const gruposCard = useMemo(() => grupos.map(mapGrupoParaCard), [grupos]);

  // ── Drawer crear/editar grupo ────────────────────────────────
  const asegurarFormularios = () => {
    if (formularios.length > 0) return;
    databaseService.admin.listarFormularios().then(setFormularios).catch(() => {});
  };

  const abrirDrawerCrear = () => {
    setGrupoEditandoId(null);
    setGrupoEditandoEstudiantes([]);
    setGrupoForm(emptyGrupoForm());
    setFormError(null);
    asegurarFormularios();
    setShowCrearGrupo(true);
  };

  const abrirDrawerEditar = async (grupo: GrupoCardData) => {
    setGrupoEditandoId(grupo.id);
    setFormError(null);
    asegurarFormularios();
    setShowCrearGrupo(true);
    setLoadingDrawer(true);
    try {
      const detalle = await databaseService.admin.obtenerGrupo(grupo.id);
      setGrupoForm({
        nombre: detalle.grupoNombre,
        formEmocionesId: detalle.formEmocionesId ?? "",
        formBienpsicId: detalle.formBienpsicId ?? "",
        formAprendizajeId: detalle.formAprendizajeId ?? "",
        alumnosNuevos: [createAlumnoRow()],
      });
      setGrupoEditandoEstudiantes(detalle.estudiantes);
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : "No se pudo cargar el grupo");
    } finally {
      setLoadingDrawer(false);
    }
  };

  const cerrarDrawer = () => {
    setShowCrearGrupo(false);
    setGrupoEditandoId(null);
    setGrupoEditandoEstudiantes([]);
    setGrupoForm(emptyGrupoForm());
    setShowImportModal(false);
    setImportResultado(null);
  };

  const updateAlumno = (index: number, field: "nombreCompleto" | "curp", value: string) => {
    setGrupoForm(prev => ({
      ...prev,
      alumnosNuevos: prev.alumnosNuevos.map((a, i) => i === index ? { ...a, [field]: value } : a),
    }));
  };
  const addAlumnoRow = () => setGrupoForm(prev => ({ ...prev, alumnosNuevos: [...prev.alumnosNuevos, createAlumnoRow()] }));
  const removeAlumnoRow = (index: number) => setGrupoForm(prev => ({
    ...prev, alumnosNuevos: prev.alumnosNuevos.filter((_, i) => i !== index),
  }));

  const handlePasteAlumnos = (event: React.ClipboardEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement;
    const cell = target.closest<HTMLInputElement>("input[data-alumno-index][data-alumno-field]");
    if (!cell) return;

    const texto = event.clipboardData.getData("text/plain");
    if (!texto.includes("\t") && !texto.includes("\n")) return;

    event.preventDefault();
    const filaInicial = Number(cell.dataset.alumnoIndex);
    const campoInicial = cell.dataset.alumnoField === "curp" ? 1 : 0;
    let filasPegadas = texto
      .replace(/\r/g, "")
      .split("\n")
      .map(fila => fila.split("\t").map(valor => valor.trim()))
      .filter(fila => fila.some(valor => valor.length > 0));

    const primeraFila = filasPegadas[0]?.map(valor => valor.toLowerCase());
    if (primeraFila?.some(valor => valor.includes("nombre")) && primeraFila.some(valor => valor.includes("curp"))) {
      filasPegadas = filasPegadas.slice(1);
    }

    setGrupoForm(prev => {
      const alumnosNuevos = [...prev.alumnosNuevos];
      filasPegadas.forEach((fila, filaOffset) => {
        const indice = filaInicial + filaOffset;
        while (!alumnosNuevos[indice]) alumnosNuevos.push(createAlumnoRow());
        const alumno = alumnosNuevos[indice]!;
        const actualizados = { ...alumno };
        fila.forEach((valor, columnaOffset) => {
          const columna = campoInicial + columnaOffset;
          if (columna === 0) actualizados.nombreCompleto = valor;
          if (columna === 1) actualizados.curp = valor;
        });
        alumnosNuevos[indice] = actualizados;
      });
      return { ...prev, alumnosNuevos };
    });
  };

  const handleGuardarGrupo = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!evaluacionId || !grupoForm.nombre.trim()) {
      setFormError("El nombre del grupo es requerido.");
      return;
    }

    const nuevosAlumnos = grupoForm.alumnosNuevos
      .filter(a => a.nombreCompleto.trim())
      .map(a => ({ nombreCompleto: a.nombreCompleto.trim(), curp: a.curp.trim() || undefined }));

    setSaving(true);
    setFormError(null);
    try {
      const cambiosGrupo = {
        nombre: grupoForm.nombre.trim(),
        formEmocionesId: grupoForm.formEmocionesId || undefined,
        formBienpsicId: grupoForm.formBienpsicId || undefined,
        formAprendizajeId: grupoForm.formAprendizajeId || undefined,
      };

      if (grupoEditandoId) {
        await databaseService.admin.actualizarGrupo(grupoEditandoId, cambiosGrupo);
        for (const alumno of nuevosAlumnos) {
          await databaseService.admin.agregarEstudiante(grupoEditandoId, alumno);
        }
      } else {
        await databaseService.admin.crearGrupo({ evaluacionId, ...cambiosGrupo, estudiantes: nuevosAlumnos });
      }

      cerrarDrawer();
      cargarEvaluacion();
      invalidarTabs();
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : "No se pudo guardar el grupo");
    } finally {
      setSaving(false);
    }
  };

  const eliminarGrupoActual = async () => {
    if (!grupoEditandoId) return;
    if (!confirm("¿Eliminar este grupo? Esta acción no se puede deshacer.")) return;
    try {
      await databaseService.admin.eliminarGrupo(grupoEditandoId);
      cerrarDrawer();
      cargarEvaluacion();
      invalidarTabs();
    } catch (err) {
      alert(err instanceof ApiError ? err.message : "No se pudo eliminar el grupo");
    }
  };

  const eliminarEstudianteExistente = async (estudianteId: string) => {
    if (!confirm("¿Quitar a este estudiante del grupo?")) return;
    try {
      await databaseService.admin.eliminarEstudiante(estudianteId);
      setGrupoEditandoEstudiantes(prev => prev.filter(e => e.estudianteId !== estudianteId));
      invalidarTabs();
    } catch (err) {
      alert(err instanceof ApiError ? err.message : "No se pudo quitar al estudiante");
    }
  };

  // ── Importar alumnos desde Excel ──────────────────────────────
  const descargarPlantilla = async () => {
    try {
      const { blob, filename } = await databaseService.admin.descargarPlantillaEstudiantes();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert(err instanceof ApiError ? err.message : "No se pudo descargar la plantilla");
    }
  };

  const handleImportarArchivo = async (event: ChangeEvent<HTMLInputElement>) => {
    const archivo = event.target.files?.[0];
    event.target.value = "";
    if (!archivo || !grupoEditandoId) return;

    setImportando(true);
    setImportResultado(null);
    try {
      const resultado = await databaseService.admin.importarEstudiantes(grupoEditandoId, archivo);
      setImportResultado(resultado);
      const detalle = await databaseService.admin.obtenerGrupo(grupoEditandoId);
      setGrupoEditandoEstudiantes(detalle.estudiantes);
      invalidarTabs();
      cargarEvaluacion();
    } catch (err) {
      alert(err instanceof ApiError ? err.message : "No se pudo importar el archivo");
    } finally {
      setImportando(false);
    }
  };

  // ── Reportes ─────────────────────────────────────────────────
  const handleUploadReporteEstudiante = async (grupoId: string, estudianteId: string, event: ChangeEvent<HTMLInputElement>) => {
    const archivo = event.target.files?.[0];
    if (!archivo || !evaluacionId) return;
    try {
      await databaseService.admin.subirReporte({ archivo, tipo: "individual", evaluacionId, estudianteId });
      cargarEvaluacion();
      setEstudiantesTabla(null);
    } catch (err) {
      alert(err instanceof ApiError ? err.message : "No se pudo subir el reporte");
    } finally {
      event.target.value = "";
    }
  };

  const handleUploadReportesBulk = async (event: ChangeEvent<HTMLInputElement>) => {
    const archivos = Array.from(event.target.files ?? []);
    event.target.value = "";
    if (!evaluacionId || archivos.length === 0) return;

    setSubiendoReportesBulk(true);
    setResultadoReportesBulk(null);
    try {
      const resultado = await databaseService.admin.subirReportesBulk(evaluacionId, archivos);
      setResultadoReportesBulk(resultado);
      cargarEvaluacion();
      setEstudiantesTabla(null);
    } catch (err) {
      alert(err instanceof ApiError ? err.message : "No se pudieron subir los reportes");
    } finally {
      setSubiendoReportesBulk(false);
    }
  };

  const handleUploadReporteGrupal = async (event: ChangeEvent<HTMLInputElement>) => {
    const archivo = event.target.files?.[0];
    if (!archivo || !evaluacionId || !grupoEditandoId) return;
    try {
      await databaseService.admin.subirReporte({ archivo, tipo: "grupal", evaluacionId, grupoId: grupoEditandoId });
      cargarEvaluacion();
    } catch (err) {
      alert(err instanceof ApiError ? err.message : "No se pudo subir el reporte grupal");
    } finally {
      event.target.value = "";
    }
  };

  const handleExportar = async () => {
    if (!evaluacionId || !grupoResultadosId) return;
    try {
      const { blob, filename } = await databaseService.admin.exportarEvaluacion(evaluacionId, grupoResultadosId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert(err instanceof ApiError ? err.message : "No se pudo exportar la evaluación");
    }
  };

  if (loadingEvaluacion) {
    return (
      <div style={{ display: "flex", minHeight: "100vh", background: COLORS.neutro50 }}>
        <Sidebar />
        <main style={{ flex: 1, padding: 40, color: COLORS.neutro500 }}>Cargando evaluación...</main>
      </div>
    );
  }

  if (error || !evaluacion) {
    return (
      <div style={{ display: "flex", minHeight: "100vh", background: COLORS.neutro50 }}>
        <Sidebar />
        <main style={{ flex: 1, padding: 40, color: COLORS.rojo600 }}>{error ?? "Evaluación no encontrada"}</main>
      </div>
    );
  }

  const estado = estadoEvaluacionUI(evaluacion);

  return (
    <div style={{ display: "flex", minHeight: "100vh", background: COLORS.neutro50, fontFamily: "system-ui, -apple-system, sans-serif" }}>
      <Sidebar />

      <main style={{ flex: 1, padding: "32px 40px", minWidth: 0 }}>
        
        {/* Header */}
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 24 }}>
          <div>
            <p style={{ margin: "0 0 4px", fontSize: 12, color: COLORS.neutro500, textTransform: "uppercase", letterSpacing: "0.06em" }}>
              Evaluación · {evaluacion.colegioNombre}
            </p>
            <h1 style={{ margin: "0 0 2px", fontSize: 26, fontWeight: 600, color: COLORS.neutro900 }}>
              {evaluacion.nombre}
            </h1>
            <p style={{ margin: 0, fontSize: 13, color: COLORS.neutro500 }}>
              {formatFecha(evaluacion.fecha)}
            </p>
          </div>
          {/* Estado */}
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 8 }}>
            <div>
                <span style={{
                padding: "6px 14px", borderRadius: 999, fontSize: 13, fontWeight: 600,
                background: estado === "publico" ? COLORS.verde50 : estado === "abierto" ? COLORS.violeta50 : COLORS.neutro100,
                color: estado === "publico" ? COLORS.verde600 : estado === "abierto" ? COLORS.violeta600 : COLORS.neutro700,
              }}>
                {ESTADO_UI_LABELS[estado]}
              </span>
              <NextEstadoBtn evaluacionId={evaluacionId} estado={estado} cargarEvaluacion={cargarEvaluacion} />              
            </div>
            
            

            {/* Enlaces */}
            <div style={{ display: "flex", gap: 8 }}>
              <ClipboardCopy label="Encuesta:" copyText={`${window.location.origin}/e/${evaluacion.codigoAcceso}`} />
              <ClipboardCopy label="Reportes:" copyText={`${window.location.origin}/reportes/${evaluacion.codigoAcceso}`} />

              
            </div>
          </div>
        </div>

        {/* KPIs */}
        <div style={{ display: "flex", gap: 12, marginBottom: 28 }}>
          <StatCard label="Grupos" value={grupos.length} active={layout === "grupos"} onClick={() => setLayout("grupos")} />
          {/*  <StatCard label="Estudiantes" value={totalAlumnos} active={layout === "estudiantes"} onClick={() => setLayout("estudiantes")} /> */}
          <StatCard label="Resultados" value={porcentajeCompletado + "%"} active={layout === "datos"} sub="de las preguntas respondidas" onClick={() => setLayout("datos")} />
          <StatCard label="Reportes" value={totalReportes} active={layout === "reportes"} onClick={() => setLayout("reportes")} />
        </div>

        {/* Layout principal */}
        {layout === "grupos" ? (
          <DetalleAnalisisGrupos
            grupos={gruposCard}
            onSelectGrupo={grupo => abrirDrawerEditar(grupo)}
            onCreateGrupo={abrirDrawerCrear}
          />
        ) : layout === "estudiantes" ? (
          <DetalleAnalisisEstudiantes
            estudiantes={estudiantesTabla ?? []}
            loading={loadingEstudiantes}
            onClose={() => setLayout("grupos")}
            onUploadReporte={handleUploadReporteEstudiante}
            onUploadReportesBulk={handleUploadReportesBulk}
            bulkUploading={subiendoReportesBulk}
          />
        ) : layout === "reportes" ? (
          evaluacionId ? <DetalleAnalisisReportes evaluacionId={evaluacionId} /> : null
        ) : (
          <DetalleAnalisisDatos
            preguntas={datosRespuestas?.preguntas ?? []}
            estudiantes={datosRespuestas?.estudiantes ?? []}
            loading={loadingDatos}
            grupos={grupos.map(grupo => ({ id: grupo.grupoId, nombre: grupo.grupoNombre }))}
            grupoSeleccionadoId={grupoResultadosId}
            onSeleccionarGrupo={grupoId => {
              setGrupoResultadosId(grupoId);
              setDatosRespuestas(null);
            }}
            onExportar={handleExportar}
            onClose={() => setLayout("grupos")}
          />
        )}
      </main>

      <Drawer open={showCrearGrupo} onClose={cerrarDrawer} title={grupoEditandoId ? "Editar grupo" : "Crear grupo"}>
        {loadingDrawer ? (
          <p style={{ fontSize: 14, color: COLORS.neutro500 }}>Cargando grupo...</p>
        ) : (
        <form onSubmit={handleGuardarGrupo} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div>
            <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: COLORS.neutro700, marginBottom: 6 }}>
              Nombre del grupo
            </label>
            <input
              value={grupoForm.nombre}
              onChange={event => setGrupoForm(prev => ({ ...prev, nombre: event.target.value }))}
              type="text"
              placeholder="Ej. 1ro A"
              style={{ width: "100%", padding: "9px 12px", border: `1px solid ${COLORS.neutro100}`, borderRadius: 8, fontSize: 14, color: COLORS.neutro900, outline: "none", boxSizing: "border-box" }}
            />
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: COLORS.neutro700 }}>
              Formularios asignados
            </label>
            {([
              ["emociones", "formEmocionesId"],
              ["bienestar_psicologico", "formBienpsicId"],
              ["aprendizaje", "formAprendizajeId"],
            ] as const).map(([categoria, campo]) => (
              <select
                key={campo}
                value={grupoForm[campo]}
                onChange={event => setGrupoForm(prev => ({ ...prev, [campo]: event.target.value }))}
                style={{ width: "100%", padding: "9px 12px", border: `1px solid ${COLORS.neutro100}`, borderRadius: 8, fontSize: 14, color: COLORS.neutro900, outline: "none", boxSizing: "border-box" }}
              >
                <option value="">Sin formulario de {CATEGORIA_LABELS[categoria]}</option>
                {formularios.filter(f => f.categoria === categoria).map(f => (
                  <option key={f.id} value={f.id}>{f.titulo}</option>
                ))}
              </select>
            ))}
          </div>

          <div style={{ border: `1px solid ${COLORS.neutro100}`, borderRadius: 12, padding: 12, background: COLORS.neutro50 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.neutro900 }}>Alumnos</div>
                <div style={{ fontSize: 12, color: COLORS.neutro500 }}>Agrega filas o pega directamente una tabla desde Excel.</div>
              </div>
              <button type="button" onClick={addAlumnoRow} style={{ padding: "8px 10px", borderRadius: 8, border: `1px solid ${COLORS.violeta100}`, background: COLORS.violeta50, color: COLORS.violeta600, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
                + Añadir fila
              </button>
            </div>

            <div onPaste={handlePasteAlumnos} style={{ overflowX: "auto", background: "#fff", border: `1px solid ${COLORS.neutro100}`, borderRadius: 10 }}>
              <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 420 }}>
                <thead>
                  <tr style={{ background: COLORS.neutro50 }}>
                    <th style={{ padding: "9px 10px", textAlign: "left", fontSize: 12, color: COLORS.neutro500, borderBottom: `1px solid ${COLORS.neutro100}` }}>Nombre</th>
                    <th style={{ padding: "9px 10px", textAlign: "left", fontSize: 12, color: COLORS.neutro500, borderBottom: `1px solid ${COLORS.neutro100}` }}>CURP</th>
                    <th style={{ width: 38, borderBottom: `1px solid ${COLORS.neutro100}` }} />
                  </tr>
                </thead>
                <tbody>
                  {grupoEditandoEstudiantes.map(estudiante => (
                    <tr key={estudiante.estudianteId}>
                      <td style={{ padding: "8px 10px", borderBottom: `1px solid ${COLORS.neutro50}`, fontSize: 13, color: COLORS.neutro900 }}>{estudiante.nombreCompleto}</td>
                      <td style={{ padding: "8px 10px", borderBottom: `1px solid ${COLORS.neutro50}`, fontSize: 13, color: COLORS.neutro500 }}>{estudiante.curp ?? ""}</td>
                      <td style={{ padding: 6, textAlign: "center", borderBottom: `1px solid ${COLORS.neutro50}` }}>
                        <button type="button" onClick={() => eliminarEstudianteExistente(estudiante.estudianteId)} style={{ border: "none", background: "transparent", color: COLORS.rojo600, cursor: "pointer", fontSize: 14 }} aria-label="Quitar alumno">
                          <i className="ti ti-trash" />
                        </button>
                      </td>
                    </tr>
                  ))}
                  {grupoForm.alumnosNuevos.map((alumno, index) => (
                    <tr key={alumno.id}>
                      <td style={{ padding: 6, borderBottom: `1px solid ${COLORS.neutro50}` }}>
                        <input
                          data-alumno-index={index}
                          data-alumno-field="nombreCompleto"
                          value={alumno.nombreCompleto}
                          onChange={event => updateAlumno(index, "nombreCompleto", event.target.value)}
                          placeholder="Nombre completo"
                          style={{ width: "100%", padding: "8px 9px", border: `1px solid ${COLORS.neutro100}`, borderRadius: 6, fontSize: 13, color: COLORS.neutro900, outline: "none", boxSizing: "border-box" }}
                        />
                      </td>
                      <td style={{ padding: 6, borderBottom: `1px solid ${COLORS.neutro50}` }}>
                        <input
                          data-alumno-index={index}
                          data-alumno-field="curp"
                          value={alumno.curp}
                          onChange={event => updateAlumno(index, "curp", event.target.value)}
                          placeholder="CURP"
                          style={{ width: "100%", padding: "8px 9px", border: `1px solid ${COLORS.neutro100}`, borderRadius: 6, fontSize: 13, color: COLORS.neutro900, outline: "none", boxSizing: "border-box" }}
                        />
                      </td>
                      <td style={{ padding: 6, textAlign: "center", borderBottom: `1px solid ${COLORS.neutro50}` }}>
                        {grupoForm.alumnosNuevos.length > 1 && (
                          <button type="button" onClick={() => removeAlumnoRow(index)} style={{ border: "none", background: "transparent", color: COLORS.neutro500, cursor: "pointer", fontSize: 14 }} aria-label="Eliminar alumno">
                            <i className="ti ti-trash" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {formError && <p style={{ margin: 0, fontSize: 13, color: COLORS.rojo600 }}>{formError}</p>}

          <div style={{ display: "flex", justifyContent: "space-between", gap: 8, paddingTop: 4 }}>
            {grupoEditandoId ? (
              <button type="button" onClick={eliminarGrupoActual} style={{
                padding: "9px 14px", borderRadius: 8, fontSize: 13,
                background: "none", border: "1px solid #FFC9C9",
                color: COLORS.rojo600, cursor: "pointer",
              }}>
                Eliminar grupo
              </button>
            ) : <span />}
            <div style={{ display: "flex", gap: 8 }}>
              <button type="button" onClick={cerrarDrawer} style={{
                padding: "9px 18px", borderRadius: 8, fontSize: 14,
                background: "none", border: `1px solid ${COLORS.neutro100}`,
                color: COLORS.neutro700, cursor: "pointer",
              }}>
                Cancelar
              </button>
              <button type="submit" disabled={saving} style={{
                padding: "9px 20px", borderRadius: 8, fontSize: 14,
                background: COLORS.violeta400, border: "none",
                color: "#fff", fontWeight: 500, cursor: saving ? "not-allowed" : "pointer", opacity: saving ? 0.6 : 1,
              }}>
                {saving ? "Guardando..." : grupoEditandoId ? "Guardar" : "Crear"}
              </button>
            </div>
          </div>
        </form>
        )}
      </Drawer>

      {showImportModal && (
        <Modal title="Importar alumnos desde Excel" onClose={() => { setShowImportModal(false); setImportResultado(null); }}>
          {!grupoEditandoId ? (
            <p style={{ margin: 0, fontSize: 14, color: COLORS.neutro500 }}>
              Primero crea el grupo (nombre y formularios) y guárdalo — luego podrás importar el listado completo de alumnos.
            </p>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <button
                type="button"
                onClick={descargarPlantilla}
                style={{
                  display: "flex", alignItems: "center", gap: 10,
                  padding: "14px 16px", borderRadius: 10,
                  border: `1px solid ${COLORS.neutro100}`, background: "#fff",
                  color: COLORS.neutro900, fontSize: 14, cursor: "pointer", textAlign: "left",
                }}
              >
                <i className="ti ti-download" style={{ fontSize: 18, color: COLORS.violeta400 }} aria-hidden="true" />
                <div>
                  <div style={{ fontWeight: 600 }}>Descargar plantilla de ejemplo</div>
                  <div style={{ fontSize: 12, color: COLORS.neutro500 }}>Úsala como base para que el archivo tenga el formato correcto.</div>
                </div>
              </button>

              <label style={{
                display: "flex", alignItems: "center", gap: 10,
                padding: "14px 16px", borderRadius: 10,
                border: `1.5px dashed ${COLORS.violeta200}`, background: COLORS.violeta50,
                color: COLORS.violeta600, fontSize: 14, cursor: importando ? "not-allowed" : "pointer",
                opacity: importando ? 0.6 : 1,
              }}>
                <i className="ti ti-upload" style={{ fontSize: 18 }} aria-hidden="true" />
                <div>
                  <div style={{ fontWeight: 600 }}>{importando ? "Subiendo..." : "Subir archivo de Excel"}</div>
                  <div style={{ fontSize: 12 }}>Carga directamente a los alumnos del grupo (.xlsx).</div>
                </div>
                <input
                  type="file"
                  accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  onChange={handleImportarArchivo}
                  disabled={importando}
                  style={{ display: "none" }}
                />
              </label>

              {importResultado && (
                <div style={{ border: `1px solid ${COLORS.neutro100}`, borderRadius: 10, padding: 12, background: COLORS.neutro50 }}>
                  <p style={{ margin: "0 0 6px", fontSize: 13, color: COLORS.verde600, fontWeight: 600 }}>
                    {importResultado.creados} alumno{importResultado.creados === 1 ? "" : "s"} agregado{importResultado.creados === 1 ? "" : "s"}.
                  </p>
                  {importResultado.errores.length > 0 && (
                    <div>
                      <p style={{ margin: "0 0 4px", fontSize: 12, color: COLORS.rojo600, fontWeight: 600 }}>
                        {importResultado.errores.length} fila{importResultado.errores.length === 1 ? "" : "s"} con problemas:
                      </p>
                      <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: COLORS.rojo600 }}>
                        {importResultado.errores.map((e, i) => (
                          <li key={i}>Fila {e.fila}: {e.motivo}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </Modal>
      )}

      {resultadoReportesBulk && (
        <Modal title="Resultado de reportes bulk" onClose={() => setResultadoReportesBulk(null)} width="min(680px, calc(100vw - 32px))">
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <p style={{ margin: 0, fontSize: 14, color: COLORS.neutro900 }}>
              {resultadoReportesBulk.asignados} de {resultadoReportesBulk.resultados.length} archivo{resultadoReportesBulk.resultados.length === 1 ? "" : "s"} asignado{resultadoReportesBulk.asignados === 1 ? "" : "s"} correctamente.
            </p>
            <div style={{ maxHeight: 420, overflowY: "auto", border: `1px solid ${COLORS.neutro100}`, borderRadius: 8 }}>
              {resultadoReportesBulk.resultados.map(resultado => {
                const asignado = resultado.estado === "asignado";
                return (
                  <div key={`${resultado.archivo}-${resultado.estudianteId ?? resultado.estado}`} style={{ padding: "9px 12px", borderBottom: `1px solid ${COLORS.neutro50}`, fontSize: 12 }}>
                    <div style={{ color: COLORS.neutro900, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{resultado.archivo}</div>
                    <div style={{ color: asignado ? COLORS.verde600 : COLORS.rojo600, marginTop: 3 }}>
                      {asignado
                        ? `Asignado a ${resultado.estudianteNombre} (${resultado.porcentaje}%)`
                        : `${resultado.motivo ?? "No asignado"}${resultado.porcentaje !== undefined ? ` (${resultado.porcentaje}%)` : ""}`}
                    </div>
                  </div>
                );
              })}
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <button type="button" onClick={() => setResultadoReportesBulk(null)} style={{ padding: "8px 14px", borderRadius: 8, border: `1px solid ${COLORS.neutro100}`, background: "#fff", color: COLORS.neutro700, cursor: "pointer" }}>
                Cerrar
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
